import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Slider,
  Paper,
  Tooltip,
} from '@mui/material';
import { TouchApp } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { PROMPTING_TOOLTIPS } from '../../../constants/GoalsConstants';

const MeasurementFields = ({
  dataPoint,
  index,
  hasFrequency,
  hasDuration,
  hasAccuracy,
  hasIndependent,
  hasYesNo,
  hasSeverity,
  hasIntensity,
  promptingType,
  handleNumericFieldChange,
  handleFieldChange,
  handlePromptingChange,
}) => {
  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  return (
    <>
    {hasFrequency && (
      <Box sx={{ display: 'flex', gap: 2, marginTop: '1rem' }}>
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          Frequency:
        </Typography>
        <TextField
          type='number'
          inputProps={{
            'aria-label': 'Without label',
            inputMode: 'numeric',
            onWheel: preventNumberScroll,
          }}
          margin='dense'
          size='small'
          value={dataPoint.frequency}
          sx={{ width: '15%' }}
          onChange={(e) =>
            handleNumericFieldChange(index, 'frequency', e.target.value)
          }
        />
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          times
        </Typography>
      </Box>
    )}
    {hasDuration && (
      <Box sx={{ display: 'flex', gap: 2, marginTop: '1rem' }}>
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          Duration:
        </Typography>
        <TextField
          type='number'
          inputProps={{
            'aria-label': 'Without label',
            inputMode: 'numeric',
            onWheel: preventNumberScroll,
          }}
          margin='dense'
          size='small'
          value={dataPoint.duration}
          sx={{ width: '15%' }}
          onChange={(e) =>
            handleNumericFieldChange(index, 'duration', e.target.value)
          }
        />
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          mins
        </Typography>
      </Box>
    )}
    {hasAccuracy && (
      <Box sx={{ display: 'flex', gap: 2, marginTop: '1rem' }}>
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          Accuracy:
        </Typography>
        <TextField
          type='number'
          inputProps={{
            'aria-label': 'Without label',
            inputMode: 'numeric',
            onWheel: preventNumberScroll,
          }}
          margin='dense'
          size='small'
          value={dataPoint.accuracy}
          sx={{ width: '15%' }}
          onChange={(e) =>
            handleNumericFieldChange(index, 'accuracy', e.target.value)
          }
        />
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          %
        </Typography>
      </Box>
    )}
    {hasIndependent && (
      <Box sx={{ display: 'flex', gap: 2, marginTop: '1rem' }}>
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          Independence:
        </Typography>
        <TextField
          type='number'
          inputProps={{
            'aria-label': 'Without label',
            inputMode: 'numeric',
            onWheel: preventNumberScroll,
          }}
          margin='dense'
          size='small'
          value={dataPoint.independence}
          sx={{ width: '15%' }}
          onChange={(e) =>
            handleNumericFieldChange(index, 'independence', e.target.value)
          }
        />
        <Typography variant='body1' sx={{ paddingTop: '15px' }}>
          %
        </Typography>
      </Box>
    )}
    {hasYesNo && (
      <Box sx={{ display: 'flex', gap: 2, marginTop: '1rem' }}>
        <Typography variant='body1' sx={{ paddingTop: '5px' }}>
          Yes/No:
        </Typography>
        <FormControl
          component='fieldset'
          sx={{ width: '15%' }}
          margin='dense'
          size='small'
        >
          <RadioGroup
            row
            value={
              dataPoint.yesNo === true
                ? 'yes'
                : dataPoint.yesNo === false
                ? 'no'
                : ''
            }
            onChange={(e) => {
              const selected = e.target.value === 'yes';
              handleFieldChange(index, 'yesNo', selected);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
            }}
          >
            <FormControlLabel
              value='yes'
              control={<Radio size='small' />}
              label='Yes'
            />
            <FormControlLabel
              value='no'
              control={<Radio size='small' />}
              label='No'
            />
          </RadioGroup>
        </FormControl>
      </Box>
    )}
    {hasSeverity && (
  <>
    <Typography gutterBottom sx={{ marginTop: 3 }}>
      Severity:
    </Typography>
    <Box sx={{ width: '80%', marginX: 'auto' }}>
      <Slider
        // convert string value ("Low") to numeric for display
        value={
          ['Low', 'Mild', 'Moderate', 'Severe', 'Extreme'].indexOf(
            dataPoint.severity
          ) + 1 || 1
        }
        onChange={(e, newValue) => {
          // map numeric slider value to text label
          const labels = ['Low', 'Mild', 'Moderate', 'Severe', 'Extreme'];
          handleFieldChange(index, 'severity', labels[newValue - 1]);
        }}
        min={1}
        max={5}
        step={1}
        marks={[
          { value: 1, label: 'Low' },
          { value: 2, label: 'Mild' },
          { value: 3, label: 'Moderate' },
          { value: 4, label: 'Severe' },
          { value: 5, label: 'Extreme' },
        ]}
        valueLabelDisplay='auto'
        valueLabelFormat={(v) =>
          ['Low', 'Mild', 'Moderate', 'Severe', 'Extreme'][v - 1]
        }
        sx={{
          color: '#9291ef',
          '& .MuiSlider-thumb': {
            backgroundColor: '#ffffff',
            border: '2px solid #9291ef',
          },
          '& .MuiSlider-track': {
            backgroundColor: '#9291ef',
          },
          '& .MuiSlider-rail': {
            backgroundColor: '#bcbcf5',
          },
        }}
      />
    </Box>
  </>
)}

    {hasIntensity && (
      <>
        <Typography gutterBottom sx={{ marginTop: 3 }}>
          Intensity:
        </Typography>
        <Box sx={{ width: '80%', marginX: 'auto' }}>
          <Slider
              value={
                ['Low', 'Mild', 'Moderate', 'Severe', 'Extreme'].indexOf(
                  dataPoint.intensity
                ) + 1 || 1
              }
              onChange={(e, newValue) => {
                // map numeric slider value to text label
                const labels = ['Low', 'Mild', 'Moderate', 'Severe', 'Extreme'];
                handleFieldChange(index, 'intensity', labels[newValue - 1]);
              }}
            min={1}
            max={5}
            step={1}
            marks={[
              { value: 1, label: 'Low' },
              { value: 2, label: 'Mild' },
              { value: 3, label: 'Moderate' },
              { value: 4, label: 'Severe' },
              { value: 5, label: 'Extreme' },
            ]}
            valueLabelDisplay='auto'
            sx={{
              color: '#9291ef',
              '& .MuiSlider-thumb': {
                backgroundColor: '#ffffff',
                border: '2px solid #9291ef',
              },
              '& .MuiSlider-track': {
                backgroundColor: '#9291ef',
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#bcbcf5',
              },
            }}
          />
        </Box>
      </>
    )}
    {promptingType && (
      <>
        {promptingType === 'P+ & P-' ? (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TouchApp sx={{ color: '#6b7280', mr: 1, fontSize: 18 }} />
              <Typography
                variant='body1'
                sx={{
                  color: '#1f2937',
                  fontWeight: 400,
                  fontSize: '1rem',
                }}
              >
                P+ & P- Values
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {['P-', 'P+'].map((label) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      color: '#374151',
                      fontWeight: 500,
                      minWidth: '30px',
                    }}
                  >
                    {label}
                  </Typography>
                  <TextField
                    type='number'
                    value={
                      label === 'P-'
                        ? dataPoint.prompting.pMinus
                        : dataPoint.prompting.pPlus
                    }
                    inputProps={{ inputMode: 'numeric', onWheel: preventNumberScroll }}
                    sx={{
                      width: 100,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6b7280',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6b7280',
                          },
                        },
                      },
                    }}
                    onChange={(e) =>
                      handlePromptingChange(
                        index,
                        label === 'P-' ? 'pMinus' : 'pPlus',
                        e.target.value
                      )
                    }
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        ) : promptingType === 'IVP, DVP, PVP, GP, PP' ? (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TouchApp sx={{ color: '#6b7280', mr: 1, fontSize: 18 }} />
              <Typography
                variant='body1'
                sx={{
                  color: '#1f2937',
                  fontWeight: 400,
                  fontSize: '1rem',
                }}
              >
                I, VP (IVP, DVP, PVP), GP, PP, ViP, M Values
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {['I', 'VP', 'IVP', 'DVP', 'PVP', 'GP', 'PP', 'ViP', 'M'].map(
                (label) => (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: '120px',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#374151',
                        fontWeight: 500,
                        minWidth: '30px',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {label}
                      <Tooltip title={PROMPTING_TOOLTIPS[label]}>
                        <InfoIcon
                          sx={{
                            fontSize: 'small',
                            ml: 0.5,
                            verticalAlign: 'middle',
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                    </Typography>
                    <TextField
                      type='number'
                      value={dataPoint.prompting[label]}
                      inputProps={{ inputMode: 'numeric', onWheel: preventNumberScroll }}
                      sx={{
                        width: 80,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6b7280',
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6b7280',
                            },
                          },
                        },
                      }}
                      onChange={(e) =>
                        handlePromptingChange(index, label, e.target.value)
                      }
                    />
                  </Box>
                )
              )}
            </Box>
          </Paper>
        ) : null}
      </>
    )}
    </>
  );
};

export default MeasurementFields;
